<template>
    <div>
		<AllInvestmentsReceivablesHead :accountId="accountId"/>
		<div class="flex filter_table">
			<div class="flex flex_column w_100">
				<AgItem :columnDefs="columnDefs"
						:rowData="rowData"
						v-on:sortChanged="sortChanged"
						:loading-warning-text="loadingWarningText"
						:key="loadingWarningText"
				></AgItem>
				<Pagination
					:totalNum="totalNum"
					:pageSize="pageSize"
					:sizeChange="sizeChange"
					:currentChange="currentChange"
					:currentPage="page"
				></Pagination>
			</div>
			<AllInvestmentsReceivablesFilter @apply-filter="applyFilter"/>
		</div>
        <AllInvestmentsReceivablesResellModal v-if="resellModalShown"
											  :data="resellModalData"
											  @close="closeResellModal"
											  @sell="confirmResell"
        />
		<AllInvestmentsReceivablesCollectionReportModal ref="collectionReportModal"/>
    </div>
</template>
<script>

import {allInvestmentsReceivablesApi} from "@/common/api/allInvestmentsReceivablesApi";
import {
	asDecimal,
	asLocalizedDate,
	asMoneyAmount,
	asPercentage
} from "@/components/templates/agGrid/formatter/valueFormatters";
import AgItem from "@/components/templates/agGrid/AgItem";
import {enumDef} from "@/common/enumDefs";
import AllInvestmentsReceivablesResellModal
	from "@/components/templates/investor/allInvestments/receivables/AllInvestmentsReceivablesResellModal";
import Pagination from "@/components/templates/common/Pagination";
import PaginationParent from "@/components/templates/common/PaginationParent";
import AllInvestmentsReceivablesFilter
	from "@/components/templates/investor/allInvestments/receivables/AllInvestmentsReceivablesFilter";
import AllInvestmentsReceivablesHead
	from "@/components/templates/investor/allInvestments/receivables/AllInvestmentsReceivablesHead";
import AllInvestmentsReceivablesCollectionReportModal
	from "@/components/templates/investor/allInvestments/receivables/AllInvestmentsReceivablesCollectionReportModal";

export default {
    extends: PaginationParent,
    components: {
        AllInvestmentsReceivablesResellModal,
		AllInvestmentsReceivablesCollectionReportModal,
		AllInvestmentsReceivablesFilter,
		AllInvestmentsReceivablesHead,
        AgItem,
        Pagination
    },
    props: {
        accountId: { type: Number, default: -1 },
        accountsAssigned: { type: Boolean },
        filters: { type: Array, default: [] }
    },
    data() {
        return {
            columnDefs: [],
            rowData: null,
            sorting: [],
			filters: [],
            resellModalShown: false,
            resellModalData: null,
        };
    },
    computed: {
        loadingWarningText() {
            return !this.accountsAssigned ? this.$t('common.noAccountsAssigned') : null
        }
    },
    beforeMount() {
        this.setColumnDefs();
        // Do not load list yet, since we are waiting for default sorting to be set
    },
    methods: {
        setColumnDefs() {
            this.columnDefs = [
                {
                    headerName: this.$t("common.receivableDetails"),
                    children: [
                        {
                            headerName: this.$t("common.debtor"),
                            field: "debtorName",
                            minWidth: 200,
                            nameType: "debtorName",
                            idType: "debtorId",
                            cellRendererFramework: "CompanyTem",
                            sortable: true
                        },
                        {
                            headerName: this.$t("common.seller"),
                            field: "sellerName",
                            minWidth: 200,
                            nameType: "sellerName",
                            idType: "sellerId",
                            cellRendererFramework: "CompanyTem",
                            sortable: true
                        },
	                    {
		                    headerName: this.$t("investor.common.table.header.invoiceNumberAndDocuments"),
		                    field: "invoiceNumber",
		                    minWidth: 175,
		                    sortable: true,
		                    cellRendererFramework: "ItemDetail",
	                    },
						{
							headerName: this.$t("common.contractNumber"),
							field: "installmentContractNumber",
							minWidth: 120,
							sortable: true
						},
                        {
                            headerName: this.$t("common.amount"),
                            headerClass: "align-right",
                            minWidth: 100,
                            field: "receivableAmount",
                            cellStyle: {'justify-content': 'flex-end'},
                            valueFormatter: asMoneyAmount(),
                            sortable: true
                        },
                        {
                            headerName: this.$t("common.dueDate"),
                            field: "dueDate",
                            minWidth: 120,
                            valueFormatter: asLocalizedDate(),
                            cellRendererFramework: "OverdueRenderer",
                            cellRendererParams: {
                            	auctionStatusField: "auctionStatus",
                                paymentStatusField: "sellerToBuyerPaymentStatus"
                            },
                            sortable: true
                        }
                    ]
                },
                {
                    headerName: this.$t("common.assignment"),
                    children: [
	                    {
		                    headerName: this.$t("common.purchaseMethod"),
		                    field: "biddingType",
		                    minWidth: 150,
		                    cellRendererFramework: "EnumAsText",
		                    enumDef: enumDef.bidType,
		                    sortable: true,
	                    },
                        {
                            headerName: this.$t("common.type"),
                            field: "postTrade",
                            minWidth: 200,
                            cellRendererFramework: "EnumAsText",
                            enumDef: enumDef.postTradeType,
                            sortable: true,
                        },
                        {
                          headerName: this.$t("common.retention"),
                          headerClass: "align-right",
                          field: "retention",
                          cellStyle: {'justify-content': 'flex-end'},
                          minWidth: 100,
                          valueFormatter: asPercentage({ showEmptyAsNotAvailable: true, decimals: 0 }),
                          sortable: true
                        },
                        {
                            headerName: this.$t("common.date"),
                            field: "assignmentDate",
                            minWidth: 100,
                            valueFormatter: asLocalizedDate(),
                            sortable: true,
	                        sort: "desc",
                        },
                        {
                            headerName: this.$t("common.payoutAmount"),
                            headerClass: "align-right",
                            field: "settlementPrice",
                            cellStyle: {'justify-content': 'flex-end'},
                            minWidth: 180,
                            valueFormatter: asMoneyAmount(),
                            sortable: true,
                        },
	                    {
		                    headerName: this.$t("investor.investments.table.header.purchaseFee"),
		                    headerClass: "align-right",
		                    field: "closingFeeAmount",
		                    cellStyle: { "justify-content": "flex-end" },
		                    minWidth: 120,
		                    maxWidth: 120,
		                    valueFormatter: asMoneyAmount()
	                    },
                        {
                            headerName: this.$t("common.expectedRoi"),
                            headerClass: "align-right",
                            field: "expectedRoi",
                            cellStyle: {'justify-content': 'flex-end'},
                            minWidth: 90,
                            valueFormatter: asDecimal({ showPercentSign: true }),
                            sortable: true,
                        }
                    ]
                },
	            {
		            headerName: this.$t("common.tci.full"),
		            children: [
			            {
				            headerName: this.$t("common.tci"),
				            headerComponentParams: {
					            tooltip: this.$t("common.tci.tooltip"),
				            },
				            field: "certificateId",
				            minWidth: 90,
				            cellRendererFramework: "BooleanAsIcon",
				            valueGetter: (params) => params.data.isBundleWithTCI || !!params.data.certificateId,
				            sortable: true,
			            },
			            {
				            headerName: this.$t("common.tci.id"),
				            field: "certificateId",
				            minWidth: 150,
				            flex: 1,
				            cellRendererFramework: "FileLink",
				            cellRendererParams: {
					            fileIdField: "tciContractFileId"
				            },
				            valueGetter: (params) => params.data.isBundleWithTCI ? 'Bundle' : params.data.certificateId,
				            sortable: true
			            },
			            {
				            headerName: this.$t("common.tci.premium"),
				            field: "insuranceGrossPremiumAmount",
				            minWidth: 90,
				            maxWidth: 90,
				            cellRendererFramework: "TCIPremium",
				            cellRendererParams: {
					            grossPremiumAmountField: "insuranceGrossPremiumAmount",
					            netPremiumAmountField: "insuranceNetPremiumAmount",
								unformattedDisplay: true
				            },
				            sortable: true
			            },
		            ]
	            },
                {
                    headerName: this.$t("common.payments"),
                    children: [
                        {
                            headerName: this.$t("common.outstanding"),
                            headerComponentParams: {
                                tooltip: this.$t("investor.investments.table.header.outstanding.tooltip"),
                            },
                            headerClass: "align-right",
                            field: "outstandingPercentage",
                            cellStyle: {'justify-content': 'flex-end'},
                            minWidth: 110,
                            valueFormatter: asDecimal({ showPercentSign: true }),
                            sortable: true,
                        },
                        {
                            headerName: this.$t("common.effectiveRoi"),
                            headerComponentParams: {
                                tooltip: this.$t("investor.investments.table.header.effectiveRoi.tooltip"),
                            },
                            headerClass: "align-right",
                            field: "effectiveRoi",
                            cellStyle: {'justify-content': 'flex-end'},
                            minWidth: 100,
                            valueFormatter: asDecimal({ showPercentSign: true }),
                            sortable: true,
                        },
                        {
                            headerName: this.$t("common.latePaymentDays"),
                            headerComponentParams: {
                                tooltip: this.$t("investor.investments.table.header.latePaymentDays.tooltip"),
                            },
                            headerClass: "align-right",
                            field: "calculatedLpd",
                            cellStyle: {'justify-content': 'flex-end'},
                            minWidth: 80,
                            valueFormatter: asDecimal( { zeroDefault: "0" }),
                            sortable: true,
                        },
                    ]
                },
                {
                    headerName: this.$t("common.actions"),
                    children: [
                        {
							minWidth: 90,
                            cellRendererFramework: "AllInvestmentsReceivablesTableOperation",
                            cellRendererParams: {
                                showResellModal: this.showResellModal,
                                requestIpu: this.confirmIpuRequest,
                                downloadDocuments: this.downloadDocuments
                            },
                            pinned: "right",
                        },
						{
							minWidth: 90,
							cellRendererFramework: "AllInvestmentsReceivablesTableCollectionOperation",
							cellRendererParams: {
								showCollectionReportModal: this.showCollectionReportModal,
								downloadAllCollectionDocuments: this.downloadAllCollectionDocuments
							},
							pinned: "right",
						}
                    ]
                }
            ];
        },
        sortChanged(sortData) {
            this.sorting = sortData;
			this.initPage();
			this.getList();
        },
        getList() {
            // Early out if the account is not yet set, showing an empty table
            if (this.accountId < 0) {
                return;
            }

            let postData = {
                columns: [],
                filters: this.filters || [],
                maxResults: this.maxResults,
                offset: this.offset,
                sorting: this.sorting
            };

            allInvestmentsReceivablesApi.inspection.list(this.accountId, postData)
                .then(res => {
                    this.setTotalNum(res.recordsTotal);
                    this.rowData = res.data;
                })
                .catch(this.toast);
        },
        closeResellModal() {
            this.resellModalShown = false;
            this.resellModalData = null;
        },
        showResellModal(data) {
            this.resellModalShown = true;
            this.resellModalData = data;
        },
		showCollectionReportModal(data) {
			this.$refs.collectionReportModal.showCollectionReportModal(data)
		},
        confirmResell(sellConfig) {
            let option = {
                title: this.$t("common.sell.confirm.title"),
                msg: this.$t("common.sell.confirm.msg"),
                confirmFun: this.resell,
                params: sellConfig
            };

            this.confirmMeg(option, true);
        },
        resell(sellConfig) {
            let postData = {
                receivableId: sellConfig.data.receivableId,
                invoiceNumber: sellConfig.data.invoiceNumber,
                price: sellConfig.params.startPrice,
                instantBuyPrice: sellConfig.params.instantBuyPrice,
                startTime: sellConfig.params.startTime,
                durationInHours: sellConfig.params.durationInHours
            };

            allInvestmentsReceivablesApi.operation.resell(postData)
                .then(() => {
                    this.closeResellModal();
                    this.toastSuccess();
                    this.getList();
                })
				.catch(this.toast);
        },
        confirmIpuRequest(data) {
            let option = {
                title: this.$t("common.requestIpu.confirm.title"),
                msg: this.$t("common.requestIpu.confirm.msg"),
                confirmFun: this.requestIpu,
                params: data
            };

            this.confirmMeg(option, true);
        },
        requestIpu(data) {
            allInvestmentsReceivablesApi.operation.requestIpu(data.receivableId)
                .then(() => {
                    this.toastSuccess();
                    this.getList();
                })
				.catch(this.toast);
        },
        downloadDocuments(data) {
            allInvestmentsReceivablesApi.inspection.downloadAllDocuments(data)
                .then(res => {
                    this.toastSuccess(this.$t("common.download.allDocuments.success.msg"));
					window.open(res.msg, '_blank');
                })
				.catch(this.toast);
        },
		downloadAllCollectionDocuments(receivableId) {
			allInvestmentsReceivablesApi.inspection.downloadAllCollectionDocuments(receivableId)
			.then(res => {
				this.toastSuccess(this.$t("common.download.allDocuments.success.msg"));
				window.open(res.msg, '_blank');
			})
			.catch(this.toast);
		},
		applyFilter(filters) {
			this.filters = filters;
		},
    },
    watch: {
        accountId() {
            this.getList();
        },
        filters() {
            this.getList();
        }
    }
};
</script>
